import React from 'react';
import { Table } from 'react-bootstrap';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { ImageUrl } from '../../utils/config/config';

/** The contest's catch-all team. It keeps its place in the list; every other team can be moved. */
export const FIELD_TEAM_NAME = 'The Field (All Remaining Teams)';

const isField = (team) => team?.name === FIELD_TEAM_NAME;

const cellStyle = { padding: '10px', border: '1px solid #ccc', textAlign: 'center' };

const TeamImage = ({ team }) => (
  <img
    src={team?.image ? `${ImageUrl}${team.image}` : '../image/team-icon.png'}
    alt={team?.name}
    style={{ width: '50px', height: '50px' }}
  />
);

/**
 * The teams with one moved from one position to another, as a drop reports it. The Field stays
 * where it was: it is not draggable, but a team dropped next to it would shift it along.
 *
 * @param {Array} teams The teams in run order.
 * @param {number} from The moved team's index.
 * @param {number} to The index it was dropped at.
 * @returns {Array} A new array.
 */
export const moveTeam = (teams, from, to) => {
  const next = Array.from(teams);
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);

  const fieldFrom = teams.findIndex(isField);
  const fieldTo = next.findIndex(isField);
  if (fieldFrom !== -1 && fieldTo !== fieldFrom) {
    const [field] = next.splice(fieldTo, 1);
    next.splice(fieldFrom, 0, field);
  }
  return next;
};

/**
 * An auction's teams in the order their lots run, set by drag and drop, on Create Auction and in
 * the Update Auction modal (#173). Both forms send teamId in the order shown here, and the server
 * opens the lots in that order. The modal's list used to have neither the hint nor The Field's
 * "Fixed Position" tag, so nothing said the order could be changed or what it meant.
 *
 * Divs rather than paragraphs: ModalBox (Outer/Modal.js) wraps the modal's whole body in a <p>.
 *
 * @param {{teams: {id: string, name: string, image: string}[], onChange: (teams: Array) => void}}
 *   props teams in run order - id stays a string, which Draggable's draggableId needs - and the
 *   callback that receives them reordered.
 */
export const AuctionTeamOrder = ({ teams, onChange }) => {
  const handleDragEnd = (result) => {
    if (!result.destination) return; // Dropped outside the list
    onChange(moveTeam(teams, result.source.index, result.destination.index));
  };

  return (
    <>
      <div className="mt-3">
        <span className="text-danger">*</span> Positions of auction teams can be changed by drag &amp;
        drop; the lots open in this order.
      </div>
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="droppable">
          {(provided) => (
            <Table className="table-team border mt-3" ref={provided.innerRef} {...provided.droppableProps}>
              <thead>
                <tr style={cellStyle}>
                  <th style={cellStyle}>S.No</th>
                  <th>Team</th>
                  <th style={cellStyle}>Name</th>
                </tr>
              </thead>
              <tbody>
                {teams.map((team, index) =>
                  !isField(team) ? (
                    <Draggable key={team.id} draggableId={team.id} index={index}>
                      {(dragProvided) => (
                        <tr
                          ref={dragProvided.innerRef}
                          {...dragProvided.draggableProps}
                          {...dragProvided.dragHandleProps}
                        >
                          <td style={{ textAlign: 'center' }}>{index + 1}</td>
                          <td style={cellStyle}>
                            <TeamImage team={team} />
                          </td>
                          <td style={cellStyle}>{team.name}</td>
                        </tr>
                      )}
                    </Draggable>
                  ) : (
                    <tr key={team.id}>
                      <td style={{ textAlign: 'center' }}>{index + 1}</td>
                      <td style={cellStyle}>
                        <TeamImage team={team} />
                      </td>
                      <td style={cellStyle}>
                        {team.name} - <span className="text-success fs-6 fw-bold">Fixed Position</span>
                      </td>
                    </tr>
                  )
                )}
              </tbody>
            </Table>
          )}
        </Droppable>
      </DragDropContext>
    </>
  );
};
